// Truecaller tab — a playground for looking up caller names through the
// admin-gated truecaller_lookup Cloud Function (functions/truecallerLookup.js).
//
// Why a playground: there is no official Truecaller lookup API. The function
// talks to the Truecaller app's private endpoints with an "installation ID"
// (the bearer token the app receives after an OTP login). Before wiring this
// into the call-center screens we need four answers, measured on OUR numbers:
// how often Truecaller has a name at all, how often it agrees with the name
// on the /users record, how fast it is, and how soon the account is
// throttled. This tab answers those and nothing else.
//
// Secrets stay server-side: the token lives at /truecaller_auth (denied to
// every client) or in functions/.env, and this tab only ever sees a masked
// copy. Results are cached server-side (/truecaller_cache), so repeating a
// test costs no Truecaller quota unless "bypass cache" is ticked, and every
// lookup is audited to /security_logs (TRUECALLER_LOOKUP).
//
// RTDB cost: this tab makes NO direct database reads. The sample buttons
// pick numbers from the users already in Redux; everything else goes through
// the function (one small read per cache entry + one stats update per call).
// The function is called at its DIRECT URL with a POST body, like the
// Maintenance runners.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FirebaseContext } from 'common';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { SectionCard, FieldLabel } from '../../components/modern';
import { formatAddisDate, formatAddisDateTime } from '../../common/exportUtils';
import { tokens as c } from '../../theme';

const FUNCTION_NAME = 'truecaller_lookup';
const MAX_PHONES = 30;
const SAMPLE_SIZE = 10;

const descSx = { color: c.INK_2, mb: 2 };
const monoSx = { fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontVariantNumeric: 'tabular-nums' };
const cellSx = { py: 0.75, verticalAlign: 'top' };
const headSx = { ...cellSx, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontWeight: 600, fontSize: 12.5 };
const statSx = { fontWeight: 700, fontSize: 24, color: c.INK, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' };
const statLabelSx = { color: c.TEXT_3, fontSize: 13 };
const quietSx = { color: c.TEXT_3 };
const warnSx = { color: c.WARNING_TEXT };
const errSx = { color: c.DANGER_TEXT };

// Region follows functions/index.js setGlobalOptions (same helper as the
// Maintenance tab): second label of a 4-label databaseURL, else us-central1.
const directFunctionRegion = (config) => {
  const parts = String((config && config.databaseURL) || '').replace(/^https?:\/\//, '').split('/')[0].split('.');
  return parts.length === 4 ? parts[1] : 'us-central1';
};

// POST JSON to the direct Cloud Function URL with the admin's ID token.
// Throws on !ok with the server's message; the parsed body rides on err.body.
const postDirectFunction = async (firebaseAuth, projectId, region, payload) => {
  const currentUser = firebaseAuth && firebaseAuth.currentUser;
  if (!currentUser) throw new Error('Not authenticated');
  const idToken = await currentUser.getIdToken();
  const url = `https://${region}-${projectId}.cloudfunctions.net/${FUNCTION_NAME}`;
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(payload || {}),
    });
  } catch (err) {
    throw new Error(`${err.message} (network or CORS — the portal origin must be allowlisted in fixCorsIssue, and the function must be deployed)`);
  }
  const text = await response.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { body = null; }
  if (!response.ok) {
    const msg = (body && (body.message || body.error)) || (text ? text.slice(0, 200) : '') || response.statusText || 'request failed';
    const e = new Error(`${msg} (HTTP ${response.status})`);
    e.body = body;
    throw e;
  }
  if (!body || typeof body !== 'object') throw new Error('empty or non-JSON response');
  return body;
};

// Addis Ababa time on a 12-hour clock, whatever the computer's zone.
const fmtTime = (ms) => (ms ? formatAddisDateTime(ms) : '—');
const fmtDate = (ms) => (ms ? formatAddisDate(ms) : '—');
const shortUid = (uid) => (uid && uid.length > 12 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid || '');
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');

const MATCH_CHIP = {
  exact: { label: 'same name', color: 'success' },
  partial: { label: 'partly same', color: 'warning' },
  none: { label: 'different', color: 'error' },
  script: { label: 'other script', color: 'default' },
};

const parsePhones = (text) => String(text || '').split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);

// Fisher–Yates on a copy; the sample buttons want a different handful each click.
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = a[i]; a[i] = a[j]; a[j] = t;
  }
  return a;
};

export default function TruecallerPlayground() {
  const { t } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const usersdata = useSelector((state) => state.usersdata);
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const isAdmin = auth?.profile?.usertype === 'admin';
  const projectId = (config && config.projectId) || 'donkey-drive-5cdd1';
  const region = directFunctionRegion(config);

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);

  const [loginPhone, setLoginPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [authBusy, setAuthBusy] = useState(null); // null | 'otp' | 'verify' | 'set' | 'clear'
  const [authMsg, setAuthMsg] = useState(null); // { severity, text }

  const [phonesText, setPhonesText] = useState('');
  const [force, setForce] = useState(false);
  const [includeDb, setIncludeDb] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [mode, setMode] = useState('auto');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [sampleMsg, setSampleMsg] = useState(null);

  const call = useCallback((payload) => postDirectFunction(firebaseAuth, projectId, region, payload), [firebaseAuth, projectId, region]);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      setStatus(await call({ action: 'status' }));
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusLoading(false);
    }
  }, [call]);

  useEffect(() => {
    if (!isAdmin) return;
    refreshStatus();
  }, [isAdmin, refreshStatus]);

  const runAuth = async (kind, payload, onOk) => {
    setAuthBusy(kind);
    setAuthMsg(null);
    try {
      const r = await call(payload);
      const ok = r.success !== false;
      setAuthMsg({ severity: ok ? 'success' : 'warning', text: r.message || (ok ? 'Done.' : 'Truecaller refused.') });
      if (ok && onOk) onOk(r);
      await refreshStatus();
    } catch (err) {
      const upstream = err.body && err.body.upstream && err.body.upstream.body ? ` — upstream: ${JSON.stringify(err.body.upstream.body).slice(0, 300)}` : '';
      setAuthMsg({ severity: 'error', text: `${err.message}${upstream}` });
    } finally {
      setAuthBusy(null);
    }
  };

  const handleSendOtp = () => runAuth('otp', { action: 'send_otp', phone: loginPhone.trim(), raw: showRaw });
  const handleVerifyOtp = () => runAuth('verify', { action: 'verify_otp', otp: otp.trim(), raw: showRaw }, () => setOtp(''));
  const handleSetToken = () => runAuth('set', { action: 'set_token', installationId: manualToken.trim(), phone: manualPhone.trim() || undefined }, () => { setManualToken(''); setManualPhone(''); });
  const handleClearToken = async () => {
    const ok = await confirmDialog({
      title: t('truecaller_forget_confirm', { defaultValue: 'Forget the stored Truecaller installation ID?' }),
      body: t('truecaller_forget_confirm_body', { defaultValue: 'Lookups stop working until a new login (or the env fallback, if one is set). The result cache and counters are kept.' }),
      confirmLabel: t('truecaller_forget_token', { defaultValue: 'Forget token' }),
      destructive: true,
    });
    if (!ok) return;
    runAuth('clear', { action: 'clear_token' });
  };

  const fillSample = (usertype) => {
    setSampleMsg(null);
    const list = (usersdata && usersdata.staticusers) || [];
    const pool = list.filter((u) => u && u.usertype === usertype && u.mobile);
    if (!pool.length) {
      setSampleMsg(`No ${usertype}s with a phone are loaded in this session (the users list loads when you open the Users pages) — paste numbers instead.`);
      return;
    }
    setPhonesText(shuffle(pool).slice(0, SAMPLE_SIZE).map((u) => u.mobile).join('\n'));
  };

  const handleLookup = async () => {
    const phones = parsePhones(phonesText);
    setLookupError(null);
    if (!phones.length) { setLookupError('Enter at least one phone number.'); return; }
    if (phones.length > MAX_PHONES) { setLookupError(`At most ${MAX_PHONES} numbers per call (one Truecaller bulk request).`); return; }
    setLookupBusy(true);
    try {
      const r = await call({ action: 'lookup', phones, force, includeDb, mode, raw: showRaw });
      setLookup(r);
      // Counters moved; refresh quietly without the spinner.
      try { setStatus(await call({ action: 'status' })); } catch (_) { /* counters only */ }
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const today = (status && status.today) || {};
  const limits = (status && status.limits) || {};
  const configured = !!(status && status.configured);
  const summary = lookup && lookup.summary;
  const results = (lookup && lookup.results) || [];
  const authDisabled = authBusy !== null || lookupBusy;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage kind="warning" title="Unofficial integration."> Truecaller publishes no lookup API; this uses the endpoints its own
        app calls, signed in as a Truecaller account whose installation ID is stored server-side. Keep it to small
        tests: results are cached for {limits.cacheTtlDays || 30} days, the function stops after {limits.dailyCap || 300} upstream
        requests a day, and every lookup is written to Security Logs with your name and the numbers asked. Use a spare
        Truecaller account (ideally Premium) — a login here may sign that account out of its phone, and heavy use
        gets accounts throttled or suspended.
      </InlineMessage>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' } }}>
        {/* ── Connection ─────────────────────────────────────────────── */}
          <SectionCard id="tc-account" icon={<VpnKeyIcon />} hue="accent" title="Truecaller account" sx={{ height: '100%' }}>
            <Box sx={{ mt: 1.5 }}>
              {statusLoading && !status ? (
                <Box display="flex" alignItems="center" gap={1} py={2}><CircularProgress size={18} /> <Typography sx={descSx} mb={0}>Checking…</Typography></Box>
              ) : statusError ? (
                <InlineMessage kind="error" sx={{ mb: 2 }}>{statusError}</InlineMessage>
              ) : (
                <Box mb={2}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                    <Chip
                      size="small"
                      color={configured ? 'success' : 'default'}
                      label={configured ? `Token set (${status.source === 'env' ? 'functions/.env' : 'portal login'})` : 'No token — lookups disabled'}
                    />
                    {configured && status.tokenMasked && <Chip size="small" variant="outlined" label={status.tokenMasked} sx={{ fontVariantNumeric: 'tabular-nums' }} />}
                    {status.phoneMasked && <Chip size="small" variant="outlined" label={`account ${status.phoneMasked}`} />}
                    {status.pending && <Chip size="small" color="warning" label={`OTP pending for ${status.pending.phoneMasked}`} />}
                    <Tooltip title="Re-read token state and today's counters">
                      <span><Button variant="text" startIcon={<RefreshIcon />} onClick={refreshStatus} disabled={statusLoading}>Refresh</Button></span>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ ...descSx, mb: 0.5 }} variant="body2">
                    Verified {fmtTime(status.verifiedAt)} · last successful lookup {fmtTime(status.lastOkAt)}
                  </Typography>
                  {status.lastError && (
                    <Typography variant="body2" sx={errSx}>
                      Last error {fmtTime(status.lastErrorAt)}: {status.lastError}
                    </Typography>
                  )}
                </Box>
              )}

              {authMsg && <InlineMessage kind={authMsg.severity} sx={{ mb: 2 }} onClose={() => setAuthMsg(null)}>{authMsg.text}</InlineMessage>}

              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontWeight: 700, mb: 0.5, color: c.INK }}>1. Sign in with a Truecaller account (OTP)</Typography>
              <Typography sx={descSx} variant="body2">
                The phone must already have a Truecaller account. Truecaller texts a code to it; the token comes back
                to the server, never to this browser.
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-end">
                <Box sx={{ minWidth: 200 }}>
                  <FieldLabel htmlFor="tc-login-phone">Account phone</FieldLabel>
                  <TextField id="tc-login-phone" size="small" fullWidth placeholder="+2519xxxxxxxx" value={loginPhone} onChange={(e) => setLoginPhone(e.target.value)} inputProps={{ inputMode: 'tel', autoComplete: 'off' }} />
                </Box>
                <Button variant="outlined" color="neutral" onClick={handleSendOtp} disabled={authDisabled || !loginPhone.trim()}>
                  {authBusy === 'otp' ? <CircularProgress size={18} color="inherit" /> : 'Send OTP'}
                </Button>
                <Box sx={{ width: 120 }}>
                  <FieldLabel htmlFor="tc-otp" disabled={!status || !status.pending}>OTP</FieldLabel>
                  <TextField id="tc-otp" size="small" fullWidth value={otp} onChange={(e) => setOtp(e.target.value)} disabled={!status || !status.pending} inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }} />
                </Box>
                <Button variant="contained" onClick={handleVerifyOtp} disabled={authDisabled || !otp.trim() || !status || !status.pending}>
                  {authBusy === 'verify' ? <CircularProgress size={18} color="inherit" /> : 'Verify'}
                </Button>
              </Box>

              <Typography sx={{ fontWeight: 700, mt: 3, mb: 0.5, color: c.INK }}>…or paste an installation ID</Typography>
              <Typography sx={descSx} variant="body2">
                If the OTP call is refused from Google&apos;s network, log in from a laptop on a normal connection
                (<code>npx truecallerjs login</code>, then <code>npx truecallerjs -i</code>) and paste the ID here.
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-end">
                {/* The token is a secret: typed masked, never shown back (the
                    server only ever returns its masked form). */}
                <Box sx={{ minWidth: { xs: '100%', sm: 320 }, flexGrow: 1 }}>
                  <FieldLabel htmlFor="tc-install-id">Installation ID</FieldLabel>
                  <TextField id="tc-install-id" size="small" fullWidth type="password" value={manualToken} onChange={(e) => setManualToken(e.target.value)} inputProps={{ autoComplete: 'off', spellCheck: false, 'data-lpignore': 'true' }} />
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <FieldLabel htmlFor="tc-manual-phone">Account phone (optional)</FieldLabel>
                  <TextField id="tc-manual-phone" size="small" fullWidth value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} inputProps={{ inputMode: 'tel', autoComplete: 'off' }} />
                </Box>
                <Button variant="outlined" color="neutral" onClick={handleSetToken} disabled={authDisabled || manualToken.trim().length < 10}>
                  {authBusy === 'set' ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                </Button>
                <Button color="error" onClick={handleClearToken} disabled={authDisabled || !configured || (status && status.source === 'env')}>
                  {authBusy === 'clear' ? <CircularProgress size={18} color="inherit" /> : t('truecaller_forget_token', { defaultValue: 'Forget token' })}
                </Button>
              </Box>
            </Box>
          </SectionCard>

        {/* ── Today ──────────────────────────────────────────────────── */}
          <SectionCard id="tc-today" icon={<ContactPhoneIcon />} hue="info" title={`Today (${today.dateKey || '—'}, Addis)`} sx={{ height: '100%' }}>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mt: 1.5, mb: 2 }}>
                {[
                  ['Numbers looked up', today.lookups],
                  ['Truecaller requests', `${today.requests || 0} / ${limits.dailyCap || 300}`],
                  ['Names found', today.named],
                  ['Cache hits', today.cacheHits],
                  ['Upstream errors', today.errors],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ minWidth: 0 }}>
                    <Typography sx={statSx}>{value === undefined ? '—' : value}</Typography>
                    <Typography sx={statLabelSx}>{label}</Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ ...statLabelSx, mb: 0.5 }}>Endpoints in use (override in functions/.env)</Typography>
              <Typography sx={monoSx}>
                {status && status.endpoints
                  ? `${status.endpoints.searchHost}\n${status.endpoints.accountHost}\n${status.endpoints.userAgent}`
                  : '—'}
              </Typography>
              <Typography sx={{ ...statLabelSx, mt: 1 }}>
                Cache {limits.cacheTtlDays ?? 30} d (no-name results {limits.negativeCacheTtlDays ?? 7} d) · {limits.bulkMax || 30} numbers per bulk request · {limits.timeoutMs ? `${limits.timeoutMs / 1000}s` : '12s'} timeout
              </Typography>
          </SectionCard>
      </Box>

      {/* ── Lookup ───────────────────────────────────────────────────── */}
      <SectionCard id="tc-lookup" icon={<SearchIcon />} hue="success" title="Look up numbers">
          <Typography sx={{ ...descSx, mt: 0.5 }} variant="body2">
            One number per line (or comma-separated), up to {MAX_PHONES}. Local forms like 09… and 9… are read as
            Ethiopian. Each number is also matched to our /users record by phone so you can see whether Truecaller&apos;s
            name agrees with the name the person registered with.
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' } }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="tc-phones">Phone numbers</FieldLabel>
              <TextField
                id="tc-phones"
                multiline
                minRows={6}
                maxRows={14}
                fullWidth
                placeholder={'+251911234567\n0911234567\n0711234567'}
                value={phonesText}
                onChange={(e) => setPhonesText(e.target.value)}
                inputProps={{ spellCheck: false }}
                sx={{ '& textarea': { fontVariantNumeric: 'tabular-nums' } }}
              />
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                <Button variant="outlined" color="neutral" onClick={() => fillSample('driver')} disabled={lookupBusy}>Sample {SAMPLE_SIZE} drivers</Button>
                <Button variant="outlined" color="neutral" onClick={() => fillSample('customer')} disabled={lookupBusy}>Sample {SAMPLE_SIZE} customers</Button>
                <Button variant="text" color="neutral" onClick={() => { setPhonesText(''); setLookup(null); setLookupError(null); setSampleMsg(null); }} disabled={lookupBusy}>Clear</Button>
              </Box>
              {sampleMsg && <Typography variant="caption" sx={warnSx}>{sampleMsg}</Typography>}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Box sx={{ maxWidth: 320 }}>
                  <FieldLabel htmlFor="tc-mode">Request mode</FieldLabel>
                  <TextField id="tc-mode" select size="small" fullWidth value={mode} onChange={(e) => setMode(e.target.value)} SelectProps={{ SelectDisplayProps: { 'aria-describedby': 'tc-mode-hint' } }}>
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="single">One /v2/search call per number</MenuItem>
                    <MenuItem value="bulk">One /v2/bulk call (up to 30)</MenuItem>
                  </TextField>
                  <Typography id="tc-mode-hint" variant="caption" sx={{ ...quietSx, display: 'block', mt: 0.5 }}>Auto: one /v2/search call for a single number, one /v2/bulk call for several.</Typography>
                </Box>
                <FormControlLabel control={<Checkbox checked={includeDb} onChange={(e) => setIncludeDb(e.target.checked)} />} label={<Typography>Compare with our /users record</Typography>} />
                <FormControlLabel control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />} label={<Typography>Bypass the cache (spends Truecaller quota)</Typography>} />
                <FormControlLabel control={<Checkbox checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} />} label={<Typography>Show the raw Truecaller JSON</Typography>} />
                <Box mt={1} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Button variant="contained" startIcon={lookupBusy ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} onClick={handleLookup} disabled={lookupBusy || authBusy !== null || !phonesText.trim()}>
                    Look up
                  </Button>
                  {!configured && <Typography variant="body2" sx={warnSx}>No token yet — only cached numbers will resolve.</Typography>}
                </Box>
              </Box>
            </Box>
          </Box>

          {lookupError && <InlineMessage kind="error" sx={{ mt: 2 }}>{lookupError}</InlineMessage>}

          {summary && (
            <Box mt={3}>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1.5}>
                <Chip size="small" label={`${summary.valid} of ${summary.asked} valid`} />
                <Chip size="small" color={summary.named ? 'success' : 'default'} label={`Truecaller named ${summary.named} (${pct(summary.named, summary.valid)})`} />
                {includeDb && <Chip size="small" label={`in our DB ${summary.dbMatched}`} />}
                {includeDb && <Chip size="small" color="success" variant="outlined" label={`same name ${summary.nameExact}`} />}
                {includeDb && <Chip size="small" color="warning" variant="outlined" label={`partly ${summary.namePartial}`} />}
                {includeDb && <Chip size="small" color="error" variant="outlined" label={`different ${summary.nameNone}`} />}
                {includeDb && summary.nameScript > 0 && <Chip size="small" variant="outlined" label={`other script ${summary.nameScript}`} />}
                <Chip size="small" variant="outlined" label={`cache hits ${summary.cacheHits}`} />
                <Chip size="small" variant="outlined" label={`${summary.upstreamRequests} upstream ${summary.mode || ''} request${summary.upstreamRequests === 1 ? '' : 's'} in ${summary.upstreamMs} ms`} />
                {summary.errorCode && <Chip size="small" color="error" label={summary.errorCode} />}
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}><strong>#</strong></TableCell>
                      <TableCell sx={headSx}><strong>Number</strong></TableCell>
                      <TableCell sx={headSx}><strong>Truecaller says</strong></TableCell>
                      <TableCell sx={headSx}><strong>Score</strong></TableCell>
                      <TableCell sx={headSx}><strong>Carrier · city</strong></TableCell>
                      <TableCell sx={headSx}><strong>Spam</strong></TableCell>
                      {includeDb && <TableCell sx={headSx}><strong>Our record</strong></TableCell>}
                      {includeDb && <TableCell sx={headSx}><strong>Match</strong></TableCell>}
                      <TableCell sx={headSx}><strong>Source</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((r, i) => {
                      const t = r.tc || {};
                      const m = r.match ? MATCH_CHIP[r.match] : null;
                      return (
                        <TableRow key={`${r.input}-${i}`} hover>
                          <TableCell sx={cellSx}>{i + 1}</TableCell>
                          <TableCell sx={cellSx}>
                            <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>{r.e164 || r.input}</Typography>
                            {r.e164 && r.e164 !== r.input && <Typography variant="caption" sx={quietSx}>from {r.input}</Typography>}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {t.name ? (
                              <>
                                <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                                {t.altName && <Typography variant="caption" sx={{ color: c.INK_2 }}>also “{t.altName}”</Typography>}
                                {(t.jobTitle || t.companyName) && <Typography variant="caption" display="block" sx={{ color: c.INK_2 }}>{[t.jobTitle, t.companyName].filter(Boolean).join(' · ')}</Typography>}
                              </>
                            ) : r.tc ? (
                              <Typography sx={quietSx}>{t.access === 'PRIVATE' ? 'private profile (no name)' : 'no name'}</Typography>
                            ) : (
                              <Typography sx={quietSx}>—</Typography>
                            )}
                            {r.error && <Typography variant="caption" display="block" sx={errSx}>{r.error}</Typography>}
                            {t.searchWarnings && t.searchWarnings.length > 0 && <Typography variant="caption" display="block" sx={warnSx}>{t.searchWarnings.join(' · ')}</Typography>}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {t.score !== null && t.score !== undefined ? t.score.toFixed(2) : '—'}
                            {t.access && <Typography variant="caption" display="block" sx={quietSx}>{t.access.toLowerCase()}{t.enhanced ? ' · enhanced' : ''}</Typography>}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography variant="body2">{[t.carrier, t.city].filter(Boolean).join(' · ') || '—'}</Typography>
                            {t.numberType && <Typography variant="caption" sx={quietSx}>{t.numberType.toLowerCase()}</Typography>}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {t.spamScore !== null && t.spamScore !== undefined
                              ? <Chip size="small" color="error" variant="outlined" label={`${t.spamScore}${t.spamReports ? ` · ${t.spamReports} reports` : ''}`} />
                              : <Typography variant="body2" sx={quietSx}>—</Typography>}
                          </TableCell>
                          {includeDb && (
                            <TableCell sx={cellSx}>
                              {r.db ? (
                                <>
                                  <Typography sx={{ fontWeight: 700 }}>{r.db.name || '(no name on record)'}</Typography>
                                  <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
                                    <Chip size="small" variant="outlined" label={r.db.usertype || '?'} color={r.db.usertype === 'driver' ? 'success' : r.db.usertype === 'customer' ? 'primary' : 'default'} />
                                    {r.db.blocked && <Chip size="small" color="error" label="blocked" />}
                                    <Typography variant="caption" sx={{ ...quietSx, fontVariantNumeric: 'tabular-nums' }}>{shortUid(r.db.uid)}</Typography>
                                  </Box>
                                  {r.db.usertype === 'driver' && (r.db.carType || r.db.vehicleNumber) && <Typography variant="caption" sx={{ color: c.INK_2 }}>{[r.db.carType, r.db.vehicleNumber].filter(Boolean).join(' · ')}</Typography>}
                                </>
                              ) : r.valid ? (
                                <Typography variant="body2" sx={quietSx}>{r.dbError ? `lookup failed: ${r.dbError}` : 'not a registered user'}</Typography>
                              ) : '—'}
                            </TableCell>
                          )}
                          {includeDb && (
                            <TableCell sx={cellSx}>
                              {m ? <Chip size="small" color={m.color} label={m.label} /> : <Typography variant="body2" sx={quietSx}>—</Typography>}
                            </TableCell>
                          )}
                          <TableCell sx={cellSx}>
                            {r.cached ? (
                              <Tooltip title={`cached ${fmtTime(r.fetchedAt)}`}><Chip size="small" variant="outlined" label={`cache · ${fmtDate(r.fetchedAt)}`} /></Tooltip>
                            ) : r.fetchedAt ? (
                              <Chip size="small" color="primary" variant="outlined" label="live" />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {showRaw && lookup && (
                <Box mt={2}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Raw Truecaller response{lookup.raw && lookup.raw.length !== 1 ? 's' : ''} ({(lookup.raw || []).length} — live calls only, cache hits have none)</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 400, overflow: 'auto', bgcolor: c.SURFACE_ALT }}>
                    <Typography component="pre" sx={{ ...monoSx, m: 0 }}>{JSON.stringify(lookup.raw || [], null, 2)}</Typography>
                  </Paper>
                  <Typography sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>Parsed rows</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 300, overflow: 'auto', bgcolor: c.SURFACE_ALT }}>
                    <Typography component="pre" sx={{ ...monoSx, m: 0 }}>{JSON.stringify({ summary: lookup.summary, upstream: lookup.upstream, results: lookup.results }, null, 2)}</Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
      </SectionCard>

      <Typography variant="caption" sx={quietSx}>
        What a call-center rollout would need after this test: a role or capability flag on the function instead of
        admin-only, a per-agent daily budget, and a name hint on the caller card that reads the server cache first.
        None of that is built yet — this tab exists to decide whether it is worth building.
      </Typography>
    </Box>
  );
}
